import { z } from 'zod';
import { OutOfScopeError } from './errors.js';

/**
 * The levels of the tree a node — and so a campaign's owner, a box setting or
 * an integration client — sits at. `self` is not one of them: it names no node.
 */
export const SCOPE_LEVELS = ['institution', 'country', 'zone', 'region', 'global'] as const;
export type ScopeLevel = (typeof SCOPE_LEVELS)[number];
export const scopeLevelSchema = z.enum(SCOPE_LEVELS);

/**
 * The levels a **role assignment** may carry: the tree's, and below them
 * `self` — *Yalnızca kendisi* — which reaches only the records the user
 * created, entered or is assigned, wherever they sit
 * (docs/decisions/0087-a-self-scope-reaches-what-the-user-entered.md).
 */
export const ROLE_SCOPE_LEVELS = ['self', ...SCOPE_LEVELS] as const;
export type RoleScopeLevel = (typeof ROLE_SCOPE_LEVELS)[number];
export const roleScopeLevelSchema = z.enum(ROLE_SCOPE_LEVELS);

const BREADTH: Record<RoleScopeLevel, number> = {
  self: -1,
  institution: 0,
  country: 1,
  zone: 2,
  region: 3,
  global: 4,
};

/**
 * A `self` scope's `id` is the **user's own id**, never a node's: the session
 * binds it, since a `self` assignment is stored with no `scope_id`. Anything
 * that treats a scope's id as a node must narrow `self` away first — the type
 * makes it: `NodeScope` is what `scopeFilter` and the tree predicates take.
 */
export type SelfScope = { readonly level: 'self'; readonly id: string };

export type NodeScope =
  | { readonly level: 'global'; readonly id: null }
  | { readonly level: Exclude<ScopeLevel, 'global'>; readonly id: string };

export type Scope = NodeScope | SelfScope;

export const globalScope: NodeScope = { level: 'global', id: null };

export function scope(level: ScopeLevel, id: string | null): NodeScope;
export function scope(level: 'self', id: string): SelfScope;
export function scope(level: RoleScopeLevel, id: string | null): Scope;
export function scope(level: RoleScopeLevel, id: string | null): Scope {
  if (level === 'global') return globalScope;
  if (!id) throw new TypeError(`scope level ${level} requires an id`);
  return { level, id };
}

export function selfScope(userId: string): SelfScope {
  return { level: 'self', id: userId };
}

export function isSelfScope(callerScope: Scope): callerScope is SelfScope {
  return callerScope.level === 'self';
}

export type ScopedRecord = {
  readonly institutionId: string | null;
  readonly countryId: string | null;
  readonly zoneId: string | null;
  readonly regionId: string | null;
  /**
   * The users the record counts as **own** for a `self` scope — a donation's
   * creator and typist, say. Absent, no `self` caller reaches the record: a
   * node, an act scope or a placement is nobody's own, so `canAccess` fails
   * closed on it.
   */
  readonly ownedBy?: readonly (string | null)[];
};

export type ScopeColumnKey = 'institutionId' | 'countryId' | 'zoneId' | 'regionId';

export const SCOPE_COLUMN: Record<Exclude<ScopeLevel, 'global'>, ScopeColumnKey> = {
  institution: 'institutionId',
  country: 'countryId',
  zone: 'zoneId',
  region: 'regionId',
};

export function canAccess(callerScope: Scope, record: ScopedRecord): boolean {
  if (callerScope.level === 'global') return true;
  if (callerScope.level === 'self') return record.ownedBy?.includes(callerScope.id) ?? false;
  const column = SCOPE_COLUMN[callerScope.level];
  return record[column] === callerScope.id;
}

/**
 * Whether a caller may place a **new** record — a donor, a box — at
 * `placement`. A node scope asks containment, exactly as `canAccess`. A `self`
 * caller places what they create at **their own desk** and nowhere else: the
 * record is theirs because they create it, and the desk is where they sit
 * (docs/decisions/0087).
 */
export function canPlace(
  callerScope: Scope,
  placement: ScopedRecord,
  seat: { readonly institutionId: string },
): boolean {
  if (callerScope.level === 'self') return placement.institutionId === seat.institutionId;
  return canAccess(callerScope, placement);
}

export function assertCanAccess(callerScope: Scope, record: ScopedRecord, resource: string): void {
  if (!canAccess(callerScope, record)) throw new OutOfScopeError(resource);
}

/**
 * The widest of a user's scopes, for their session. `self` is the narrowest,
 * so it governs only a user whose **every** assignment is `self`; one broader
 * assignment and the session is that one's (docs/decisions/0087).
 */
export function widest(scopes: readonly Scope[]): Scope {
  let best: Scope = scopes[0] ?? globalScope;
  for (const candidate of scopes) {
    if (BREADTH[candidate.level] > BREADTH[best.level]) best = candidate;
  }
  return best;
}

export type ScopeFilter = { readonly column: ScopeColumnKey; readonly value: string } | null;

export function scopeFilter(callerScope: NodeScope): ScopeFilter {
  if (callerScope.level === 'global') return null;
  return { column: SCOPE_COLUMN[callerScope.level], value: callerScope.id };
}
